import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { CustomerFinishedGood } from '../../../services/report.service';
import { useLanguage } from '../../../features/language/useLanguage';

const BAR_COLOR = '#FF8F00'; // primary-500 — signature amber
const ROW_HEIGHT = 40; // px per finished-good bar row
const MIN_HEIGHT = 160;

interface ChartRow {
  itemId: string;
  name: string;
  totalPcs: number;
}

interface TooltipEntry {
  value?: number;
  payload?: ChartRow;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  pcsLabel: string;
}

const ChartTooltip: React.FC<ChartTooltipProps> = ({ active, payload, pcsLabel }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div
      className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-lg"
      data-test-id="customer-fg-tooltip"
    >
      <p className="text-xs font-semibold text-gray-900">{row.name}</p>
      <p className="mt-0.5 text-xs text-gray-600">
        <span className="font-semibold tabular-nums text-gray-900">{row.totalPcs}</span> {pcsLabel}
      </p>
    </div>
  );
};

interface CustomerPurchaseChartProps {
  finishedGoods: CustomerFinishedGood[];
  onFinishedGoodClick: (itemId: string) => void;
}

export const CustomerPurchaseChart: React.FC<CustomerPurchaseChartProps> = ({
  finishedGoods,
  onFinishedGoodClick,
}) => {
  const { t } = useLanguage();
  const rows = useMemo<ChartRow[]>(
    () =>
      [...finishedGoods]
        .sort((a, b) => b.totalPcs - a.totalPcs || a.name.localeCompare(b.name))
        .map(fg => ({ itemId: fg.itemId, name: fg.name, totalPcs: fg.totalPcs })),
    [finishedGoods]
  );

  if (rows.length === 0) {
    return <p className="text-sm text-gray-500">{t('reports.customer.emptyChart')}</p>;
  }

  const innerHeight = Math.max(MIN_HEIGHT, rows.length * ROW_HEIGHT);

  return (
    <div className="max-h-[420px] overflow-y-auto" data-test-id="customer-purchase-chart">
      <div style={{ height: innerHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={rows}
            margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
            barCategoryGap="28%"
            maxBarSize={28}
            onClick={(state) => {
              // Recharts v3 types activeTooltipIndex as `string | null` (the
              // category index as a string, e.g. "0") — not a number — so we
              // coerce and bounds-check rather than `typeof === 'number'`.
              const raw = state?.activeTooltipIndex;
              if (raw == null || raw === '') return;
              const idx = Number(raw);
              if (Number.isInteger(idx) && idx >= 0 && idx < rows.length) {
                onFinishedGoodClick(rows[idx].itemId);
              }
            }}
          >
            <CartesianGrid horizontal={false} stroke="#f1f0ee" />
            <XAxis
              type="number"
              allowDecimals={false}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              tick={{ fontSize: 11, fill: '#9ca3af' }}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={140}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: '#6b7280' }}
            />
            <Tooltip
              cursor={{ fill: 'rgba(255, 143, 0, 0.06)' }}
              content={<ChartTooltip pcsLabel={t('reports.customer.table.pcs')} />}
            />
            <Bar dataKey="totalPcs" fill={BAR_COLOR} radius={[0, 4, 4, 0]} cursor="pointer" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
